import { FilterGroup } from './filter-group';
import { ReplaySubject } from 'rxjs';
import { FilterItem } from './filter-item';
import { IFilterAggregation } from '../models/filter-aggregation.interface';
import { FilterType } from '../enums/filter-type';
import { FilterItemType } from '../enums/filter-item-type';
import { FilterGroupType } from '../enums/filter-group-type';
import { IFilterAggregationItem } from '../models/filter-aggregation-item.interface';

export class FilterCollection {
  private groups: FilterGroup[] = [];
  private groupsSubject = new ReplaySubject<FilterGroup[]>(1);
  readonly Groups$ = this.groupsSubject.asObservable();
  get Groups(): FilterGroup[] {
    return this.groups;
  }

  private filtersChangedSubject = new ReplaySubject<FilterItem[]>(1);
  readonly filtersChanged$ = this.filtersChangedSubject.asObservable();

  addFilterGroups(...groups: FilterGroup[]): void {
    if (groups.length > 0) {
      this.groups.push(...groups);
      this.groupsSubject.next(this.groups);
    }
  }

  applyAggregations(aggregations: IFilterAggregation[]): void {
    aggregations.forEach(a => {
      const filterGroup = this.groups.find(group => group.GroupType.toString() === a.aggregationName);
      if (filterGroup != null) {
        if (filterGroup.FilterType !== FilterType.Range) {
          const filterItems: FilterItem[] = filterGroup.Items ?? [];
          a.items.forEach(i => {
            let filterItem = filterItems.find(x => x.ItemType === i.value);
            let needToInsert = false;
            if (!filterItem) {
              filterItem = new FilterItem(filterGroup, i.value, filterGroup.GetItemName(i.value), false, false, false, false, false);
              needToInsert = true;
            }
            filterItem.setCount(i.docCount);
            filterItem.setIsCountVisible(true);
            filterItem.setIsItemVisible(true);
            if (needToInsert) {
              filterItems.push(filterItem);
            }
            if (filterItem.ItemType === FilterItemType.Min) {
              filterItem.setValue(i.range.Min.toString());
            } else if (filterItem.ItemType === FilterItemType.Max) {
              filterItem.setValue(i.range.Max.toString());
            }
          });
          filterGroup.setItems(filterItems);
          filterGroup.setIsVisible(filterGroup.Items.some(gi => gi.IsItemVisible));
        }
      }
    });

    const filterItems = ([] as FilterItem[]).concat(...this.groups.map(g => g.Items));
    const aggregationItems = ([] as IFilterAggregationItem[]).concat(...aggregations.map(a => a.items));

    const noResultItems = filterItems.filter(i => !aggregationItems.some(gi => gi.value === i.ItemType.toString()));
    noResultItems.forEach(i => {
      if (i.Group.IsActive) {
        i.setIsCountVisible(false);
      } else {
        i.setIsItemVisible(false);
      }
      i.setCount(0);
    });

    const noResultGroups = this.groups
      .filter(g => !aggregations.some(a => a.aggregationName === g.GroupType.toString()))
      .concat(
        this.groups.filter(g => !g.IsActive && g.GroupType !== FilterGroupType.CharacterLength && g.GroupType !== FilterGroupType.PriceRange && g.Items.every(i => i.Count === 0))
      );
    noResultGroups.forEach(g => g.setIsVisible(false));

    this.groups.forEach(g => g.setIsActive(false));
  }

  reset(): void {
    this.groups.forEach(g => g.reset());
    const filterItems = this.groups.map(g => g.Items).reduce((a, b) => a.concat(b));
    this.filtersChangedSubject.next(filterItems);
  }

  raiseFiltersChanged(...items: FilterItem[]): void {
    this.filtersChangedSubject.next(items);
  }
}
